import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Text } from '@modusign/design-system';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { useForm } from '@app/features/form';
import { Breakpoint, getIsMinimized } from '@app/features/formRecommendation';
import pushAdobeDataLayer from '@app/libs/analytics/adobe';

function getButtonBoxWidthRatio(breakpoint: Breakpoint) {
  switch (breakpoint) {
    case 'md':
      return 1 / 3;
    case 'lg':
      return 1 / 4;
    case 'xl':
      return 1 / 5;
    default:
      return 1;
  }
}

interface ButtonBoxProps {
  breakpoint: Breakpoint;
  formId: string;
}

function ButtonBox({ breakpoint, formId }: ButtonBoxProps) {
  const history = useHistory();
  const isMobileOrSmViewport = /mobile|sm/.test(breakpoint);
  const isMinimized = useSelector(getIsMinimized);

  const { data: form } = useForm(formId);

  const handleButtonBoxClick = () => {
    pushAdobeDataLayer({ event: 'clickRecommendedForm', form: { form } });
    if (/mobile/.test(breakpoint)) {
      history.push(`/form-preview/${formId}`);
      return;
    }
    history.push(`/draft/temp?formId=${formId}&from=form`);
  };

  return (
    <Box
      maxHeight={isMobileOrSmViewport ? '50%' : undefined}
      sx={{
        '&:hover': {
          '& .button-box': {
            borderColor: 'boxBorderEmphasisBase',
          },
          '& .best': {
            color: 'textEmphasisLight',
          },
          '& .title': {
            color: 'textEmphasisBase',
          },
        },
      }}
      width={getButtonBoxWidthRatio(breakpoint)}
    >
      <Flex
        as="button"
        backgroundColor="boxBackgroundNeutralLight"
        borderColor="boxBorderNeutralBase"
        borderRadius="4px"
        borderStyle="solid"
        borderWidth="1px"
        className="button-box"
        disabled={isMinimized}
        flexDirection="column"
        height="100%"
        p={5}
        sx={{
          '&:hover': {
            cursor: 'pointer',
            '& .preview-button': {
              visibility: 'visible',
              color: isMobileOrSmViewport ? 'textEmphasisBase' : undefined,
            },
          },
        }}
        textAlign="start"
        width="100%"
        onClick={handleButtonBoxClick}
      >
        <Flex alignItems="center" justifyContent="space-between" width="100%">
          <Flex alignItems="center">
            <Text color="textNeutralVeryLight" fontSize="xs" fontWeight="medium" mr="2px">
              {(form?.tags.length !== 0 && form?.tags[0].name) || ''}
            </Text>
            <Text className="best" color="textEmphasisBase" fontSize="xs" fontWeight="medium">
              Best
            </Text>
          </Flex>
          <Box
            className="preview-button"
            color={isMobileOrSmViewport ? 'textNeutralUltraLight' : 'textNeutralLight'}
            sx={{
              visibility: isMobileOrSmViewport && !isMinimized ? 'visible' : 'hidden',
              '&:hover': {
                color: 'textEmphasisBase',
              },
            }}
          >
            <FontAwesomeIcon icon={faPenToSquare} width="11px" />
            <Text fontSize="xs" fontWeight="medium" ml="2px">
              미리보기
            </Text>
          </Box>
        </Flex>

        <Text
          as="p"
          className="title"
          color="textNeutralSlightlyLight"
          fontSize="md"
          fontWeight="medium"
          mt={3}
          sx={{
            overflow: 'hidden',
            wordWrap: 'break-word',
          }}
        >
          {form?.name}
        </Text>
      </Flex>
    </Box>
  );
}

export default ButtonBox;
